import { Temporal } from 'temporal-polyfill';
import { expandOccurrences } from './recurrence';
import type {
  ClassSeries,
  Location,
  Occurrence,
  OccurrenceClaim,
  OccurrenceOverride,
} from './types';

/**
 * How far outside the requested window the rule is expanded before overrides are
 * applied.
 *
 * A moved occurrence keeps its original identity but lands on a new date, so a
 * class whose rule fires just outside the window can move *into* it. Expanding a
 * padded window catches that. The padding bounds how far a reschedule can reach:
 * moves of up to a month are honoured, which comfortably covers real use while
 * keeping expansion cheap.
 */
const MOVE_PADDING_DAYS = 31;

export interface ScheduleRequest {
  readonly series: readonly ClassSeries[];
  readonly locations: readonly Location[];
  readonly rangeStart: Temporal.PlainDate;
  readonly rangeEnd: Temporal.PlainDate;
  readonly overrides?: readonly OccurrenceOverride[];
  /** Which occurrences of shared series are the instructor's. */
  readonly claims?: readonly OccurrenceClaim[];
}

export interface Schedule {
  /** Occurrences whose effective date falls in the window, ascending by instant. */
  readonly occurrences: Occurrence[];
  /**
   * Series that could not be expanded because their location no longer exists.
   *
   * A class's timezone comes from its location, so there is no honest way to
   * place it on a calendar without one. Rather than guessing a zone and showing a
   * wrong time, these are reported so the UI can say so out loud.
   */
  readonly unresolvedSeriesIds: string[];
}

/**
 * Whether a series owns its occurrences only through claims.
 *
 * True for shared rotations, and for REPEATING sub classes — covering someone
 * else's recurring slot means picking the specific days you sub, exactly like
 * picking turns in a rotation. A one-time sub class is deliberately NOT
 * claim-based: its single date is the covered day, so it is fully owned and
 * schedules like any other class.
 */
export function isClaimBased(
  series: Pick<ClassSeries, 'shared' | 'isSub' | 'recurrence'>,
): boolean {
  return series.recurrence.freq !== 'once' && (series.shared || series.isSub);
}

export function expandSchedule(request: ScheduleRequest): Schedule {
  const liveLocations = request.locations.filter((location) => location.deletedAt === null);
  const zoneByLocation = new Map(liveLocations.map((location) => [location.id, location.timeZone]));

  const overrideByKey = new Map(
    (request.overrides ?? [])
      .filter((override) => override.deletedAt === null)
      .map((override) => [String(override.occurrenceKey), override]),
  );

  // Claims key on the ORIGINAL date, like overrides, so a claimed class that is
  // later moved stays the instructor's.
  const claimedKeys = new Set(
    (request.claims ?? [])
      .filter((claim) => claim.deletedAt === null)
      .map((claim) => String(claim.occurrenceKey)),
  );

  const paddedStart = request.rangeStart.subtract({ days: MOVE_PADDING_DAYS });
  const paddedEnd = request.rangeEnd.add({ days: MOVE_PADDING_DAYS });

  const occurrences: Occurrence[] = [];
  const unresolvedSeriesIds: string[] = [];

  for (const series of request.series) {
    const timeZone = zoneByLocation.get(series.locationId);
    if (timeZone === undefined) {
      // Only worth reporting if the series would otherwise have produced dates.
      if (series.active && series.deletedAt === null) {
        unresolvedSeriesIds.push(series.id);
      }
      continue;
    }

    const expanded = expandOccurrences({
      series,
      timeZone,
      rangeStart: paddedStart,
      rangeEnd: paddedEnd,
    });

    for (const occurrence of expanded) {
      // A claim-based series (shared rotation, or a repeating sub engagement)
      // only puts the instructor's own turns on the schedule: an occurrence
      // nobody claimed is someone else's class and simply does not exist
      // here. The claim keys the ORIGINAL date, so a claimed class that was
      // later moved still surfaces (and still moves).
      if (isClaimBased(series) && !claimedKeys.has(String(occurrence.key))) {
        continue;
      }
      const override = overrideByKey.get(String(occurrence.key));
      const effective =
        override === undefined
          ? occurrence
          : applyOverride(occurrence, override, series, zoneByLocation);

      if (withinWindow(effective.date, request.rangeStart, request.rangeEnd)) {
        occurrences.push(effective);
      }
    }
  }

  occurrences.sort(
    (a, b) => Temporal.ZonedDateTime.compare(a.start, b.start) || a.title.localeCompare(b.title),
  );

  return { occurrences, unresolvedSeriesIds };
}

function withinWindow(date: string, start: Temporal.PlainDate, end: Temporal.PlainDate): boolean {
  return date >= start.toString() && date <= end.toString();
}

/**
 * Folds one override into the occurrence the rule produced.
 *
 * `key` and `originalDate` are untouched no matter what changes, so a class that
 * has already been moved can be moved again — or reset — without ambiguity.
 */
export function applyOverride(
  occurrence: Occurrence,
  override: OccurrenceOverride,
  series: ClassSeries,
  zoneByLocation: ReadonlyMap<string, string>,
): Occurrence {
  if (override.status === 'cancelled') {
    return { ...occurrence, status: 'cancelled' };
  }

  const locationId = override.newLocationId ?? series.locationId;
  // Fall back to the original zone if the override points at a removed location,
  // so a stale reference degrades to a visible-but-unmoved class.
  const timeZone = zoneByLocation.get(locationId) ?? occurrence.timeZone;
  const date = override.newDate ?? occurrence.originalDate;
  const time = override.newStartTime ?? series.startTime;

  const start = Temporal.PlainDate.from(date).toZonedDateTime({
    timeZone,
    plainTime: Temporal.PlainTime.from(time),
  });

  return {
    ...occurrence,
    date,
    locationId,
    timeZone,
    start,
    end: start.add({ minutes: series.durationMinutes }),
    status: 'scheduled',
    rescheduled:
      date !== occurrence.originalDate ||
      time !== series.startTime ||
      locationId !== series.locationId,
  };
}

/** Occurrences bucketed by their effective local date, for calendar rendering. */
export function groupByDate(occurrences: readonly Occurrence[]): Map<string, Occurrence[]> {
  const byDate = new Map<string, Occurrence[]>();
  for (const occurrence of occurrences) {
    const bucket = byDate.get(occurrence.date);
    if (bucket === undefined) {
      byDate.set(occurrence.date, [occurrence]);
    } else {
      bucket.push(occurrence);
    }
  }
  return byDate;
}
